import classNames from 'classnames';
import React from 'react';
import { defaultContentAreaRange } from '../content-area/content-area';
import { ContentGridContext, useContentGrid } from './content-grid-context';
import styles from "./content-grid-item.module.css";

/**
 * Parses all props that define the gridColumn property.
 *
 * @returns an object defining the gridColumn property for each content area range
 */
const parseRangeProps = props => {
  // For each range, a defined column prop will take precedence over its corresponding span prop
  // It's possible that neither is defined. In which case this implicitly returns undefined.
  const defineForRange = (spanProp, columnProp) => {
    // For this range, the column count is defined by a *Column prop
    if (typeof columnProp !== 'undefined') {
      return {
        isSpan: false,
        span: 0,
        gridColumn: columnProp !== null && columnProp !== void 0 ? columnProp : '',
        // If column prop is null, then this item is omitted from the grid
        isOmitted: columnProp === null
      };
    } // For this range, the column count is defined by a *Span prop


    if (spanProp) {
      return {
        isSpan: true,
        span: spanProp,
        gridColumn: `span ${spanProp}`,
        isOmitted: false
      };
    } // No definition for this range; it will cascade

  }; // To implement the cascade of column definitions for each range, include for
  // convenience `default` and `wide` as virtual ranges. `default` is smaller than `xxxs`,
  // and `wide` is in-between `sm` and `md`.


  // For any range that does not have an explicit definition from props, define them
  // by recursively calling this function for the next most narrow range.
  // For example, if `sm` is missing, then define `sm` with the definition for
  // `xs`.
  const cascade = (range, columnDefinitions) => {
    // Defines the recursive relationship between ranges. Each is linked to
    // the next most narrow range through the function call.
    const cascades = {
      // The base condition for the recursive algorithm
      default: () => ({
        isSpan: true,
        span: 1,
        gridColumn: 'span 1',
        isOmitted: false
      }),
      xxxs: () => cascade('default', columnDefinitions),
      xxs: () => cascade('xxxs', columnDefinitions),
      xs: () => cascade('xxs', columnDefinitions),
      sm: () => cascade('xs', columnDefinitions),
      wide: () => cascade('sm', columnDefinitions),
      md: () => cascade('wide', columnDefinitions),
      lg: () => cascade('md', columnDefinitions),
      xl: () => cascade('lg', columnDefinitions),
      xxl: () => cascade('xl', columnDefinitions)
    };
    const columnDefinition = columnDefinitions[range]; // If the definition from props for this range is undefined, then
    // recursively call up the chain of ranges.

    return typeof columnDefinition !== 'undefined' ? columnDefinition : cascades[range]();
  }; // gridColumn definitions explicitly provided via props, including virtual ranges


  const columnDefinitions = {
    default: defineForRange(props.defaultSpan, props.defaultColumn),
    xxxs: defineForRange(props.xxxsSpan, props.xxxsColumn),
    xxs: defineForRange(props.xxsSpan, props.xxsColumn),
    xs: defineForRange(props.xsSpan, props.xsColumn),
    sm: defineForRange(props.smSpan, props.smColumn),
    wide: defineForRange(props.wideSpan, props.wideColumn),
    md: defineForRange(props.mdSpan, props.mdColumn),
    lg: defineForRange(props.lgSpan, props.lgColumn),
    xl: defineForRange(props.xlSpan, props.xlColumn),
    xxl: defineForRange(props.xxlSpan, props.xxlColumn)
  }; // gridColumn definitions after range cascade is applied

  return new Map([['xxxs', cascade('xxxs', columnDefinitions)], ['xxs', cascade('xxs', columnDefinitions)], ['xs', cascade('xs', columnDefinitions)], ['sm', cascade('sm', columnDefinitions)], ['md', cascade('md', columnDefinitions)], ['lg', cascade('lg', columnDefinitions)], ['xl', cascade('xl', columnDefinitions)], ['xxl', cascade('xxl', columnDefinitions)]]);
};
/**
 * Returns the number of columns spanned by a `grid-column` property within a
 * grid of given `columns`. Supports a limited syntax for `grid-column`.
 *
 * @param gridColumn internal representation of a `grid-column` property
 * @param columns the number of columns in the grid
 * @returns the number of columns spanned
 *
 * @internal
 */
// eslint-disable-next-line max-statements


export const columnsSpanned = (columnDefinition, columns) => {
  var _gridColumnNoSpace$ma, _gridColumnNoSpace$ma2, _gridColumnNoSpace$ma3, _gridColumnNoSpace$ma4;

  let match;
  const {
    gridColumn,
    isOmitted
  } = columnDefinition; // Item is omitted

  if (isOmitted) {
    return null;
  } // Item span is defined with a number of columns; don't bother parsing gridColumn


  if (columnDefinition.isSpan) {
    return columnDefinition.span;
  } // Normalize negative line numbers into positive line numbers. For example,
  // with 4 columns, -1 is equivalent to 5; -2 equivalent to 4.


  const positive = line => line >= 0 ? line : line + lines + 1; // Assumes endLine is larger than startLine, and that they are index from the
  // start of the grid (i.e., -1 refers to the line before line 1, rather than the last line)


  const spanWithinGrid = (startLine, endLine) => Math.min(endLine, lines) - Math.max(startLine, 1);

  const lines = columns + 1;
  const gridColumnNoSpace = gridColumn.replaceAll(' ', ''); // Match:
  // auto

  if (gridColumnNoSpace === 'auto') {
    return 1;
  } // Match:
  // auto / span N   ('auto / span 5')
  // start / span N  ('5 / span 2')
  // span N          ('span 5')


  match = (_gridColumnNoSpace$ma = gridColumnNoSpace.match(/^((auto|(?<start>-?\d+))\/)?span(?<span>\d+)$/)) === null || _gridColumnNoSpace$ma === void 0 ? void 0 : _gridColumnNoSpace$ma.groups;

  if (match) {
    // If start is omitted, assume it is 1. Assume that if there is insufficient
    // room on the current grid row, then the item will be bumped down to an open
    // row, starting at line 1. (This is the same as if start were 'auto'.)
    const startMatch = match.start ? parseInt(match.start, 10) : 1;
    const spanMatch = parseInt(match.span, 10);

    if (startMatch === 0 || spanMatch === 0) {
      return 1;
    }

    const start = positive(startMatch);
    const end = start + spanMatch;
    return spanWithinGrid(start, end);
  } // Match:
  // span N / end  ('span 2 / -1')


  match = (_gridColumnNoSpace$ma2 = gridColumnNoSpace.match(/^span(?<span>\d+)\/(?<end>-?\d+)$/)) === null || _gridColumnNoSpace$ma2 === void 0 ? void 0 : _gridColumnNoSpace$ma2.groups;

  if (match) {
    const spanMatch = parseInt(match.span, 10);
    const endMatch = parseInt(match.end, 10);

    if (spanMatch === 0 || endMatch === 0) {
      return 1;
    }

    const end = positive(endMatch);
    const start = end - spanMatch;
    return spanWithinGrid(start, end);
  } // Match
  // start / end  ('1 / 4')


  match = (_gridColumnNoSpace$ma3 = gridColumnNoSpace.match(/^(?<start>-?\d+)\/(?<end>-?\d+)?$/)) === null || _gridColumnNoSpace$ma3 === void 0 ? void 0 : _gridColumnNoSpace$ma3.groups;

  if (match) {
    const startMatch = parseInt(match.start, 10);
    const endMatch = parseInt(match.end, 10); // Follows apparent browser behavior for invalid start / end

    if (startMatch === 0 || endMatch === 0 || startMatch === endMatch) {
      return 1;
    } // When '4 / 2', browsers interpret as '2 / 4'


    const start = Math.min(positive(startMatch), positive(endMatch));
    const end = Math.max(positive(startMatch), positive(endMatch)); // Count all spanned columns that fall within the grid

    return spanWithinGrid(start, end);
  } // Match:
  // start  ('6')


  match = (_gridColumnNoSpace$ma4 = gridColumnNoSpace.match(/^(?<start>-?\d+)$/)) === null || _gridColumnNoSpace$ma4 === void 0 ? void 0 : _gridColumnNoSpace$ma4.groups;

  if (match) {
    const startMatch = parseInt(match.start, 10);

    if (startMatch === 0) {
      return 1;
    }

    const start = positive(startMatch);
    const end = start + 1;

    if (start >= lines) {
      return 0;
    }

    return spanWithinGrid(start, end);
  } // No match


  return 1;
};
/**
 * ### ContentGridItem
 *
 * Component for positioning other components and elements on the grid created
 * by `ContentGrid`.
 *
 * @remarks
 *
 * `ContentGridItem` must be an immediate child of a `ContentGrid` in the
 * rendered DOM.
 *
 * This component has props to specify where on the grid the item should render
 * at each content area range. Based on these props definitions for each range,
 * `ContentGridItem` will automatically adjust to changes in the content area
 * width and will reposition itself.
 *
 * For example, `<ContentGridItem defaultSpan={2} mdSpan={6}>...</ContentGridItem>`
 * will give this item a column span of 2 by default. If the content area range
 * is at `md` or wider, the item will span 6 columns.
 *
 * #### Column Props for Ranges
 *
 * (@see `ContentArea` for more details on content area ranges.)
 *
 * By default, the item will span 1 column on the grid. But, you can use props
 * to define which columns the item should span at each content range.
 *
 * Internally, at each content area range, the item is positioned on the content
 * grid with a
 * {@link https://developer.mozilla.org/en-US/docs/Web/CSS/grid-column|`grid-column` CSS property}.
 * For convenience, there are column definition props to express these placement
 * definitions with shorthand.
 *
 * For each range, there is a prop to define the `grid-column` property directly
 * (`defaultColumn`, `xxxsColumn`-`xxlColumn`: "column props"). These props support
 * a limited syntax for `grid-column`. Additionally, for each range, there is a
 * shorthand prop to simply define the number of columns the item will span
 * (`defaultSpan`, `xxxsSpan`-`xxlSpan`: "span props").
 *
 * Columns props take precedence over span props at each range.
 *
 * The column definitions at each range cascade upwards, from most narrow to most
 * wide. This means that you do not need to explicitly specify column definitions
 * at each range. For example, if you specify a column definition at range `xs`,
 * then that definition will apply at all ranges up the scale (`sm`, `md`, and
 * so on), until a wider range is defined.
 *
 * For example:
 *
 * `<ContentGridItem xxxsSpan={1} mdSpan={6} xlSpan={8} ... />` will span the
 * following columns:
 *
 * - `xxxs`: 1
 * - `xxs`: 1
 * - `xs`: 1
 * - `sm`: 1
 * - `md`: 6
 * - `lg`: 6
 * - `xl`: 8
 * - `xxl`: 8
 *
 * ##### Aliases
 *
 * Ranges `xxxs` and `md` are special. `xxxs` is the smallest range. `md` is the
 * smallest range where the grid has 12 columns. To make these special ranges
 * easier to use, there alias props:
 *
 * - `defaultColumn` is an alias for `xxxsColumn`
 * - `defaultSpan` is an alias for `xxxsSpan`
 * - `wideColumn` is an alias for `mdColumn`
 * - `wideSpan` is an alias for `mdSpan`
 *
 * Consequently, to specify an item that spans 1 column at narrow ranges (when
 * the grid has 4 columns), and then 3 columns at wide ranges (when the grid has
 * 12 columns):
 *
 * ```tsx
 * <ContentGridItem defaultSpan={1} wideSpan={3} />    // is equivalent to
 * <ContentGridItem xxxsSpan={1} mdSpan={3} />
 * ```
 *
 * ##### Column Syntax
 *
 * Using the span props will handle most cases. But for situations when you
 * might want to use a column offset or want to define item placement relative
 * to the _end_ of the grid, you can specify the `grid-column` value for each
 * range using the column props. However, they support a limited syntax from the
 * full {@link https://developer.mozilla.org/en-US/docs/Web/CSS/grid-column|`grid-column` syntax}:
 *
 * ```tsx
 * // start / end
 * <ContentGridItem defaultColumn="1 / -1" mdColumn="8 / 10" />
 *
 * // start
 * <ContentGridItem defaultColumn="-1" mdColumn="3" />
 *
 * // start / span N
 * <ContentGridItem defaultColumn="1 / span 2" mdColumn="5 / span 4" />
 *
 * // span N / end
 * <ContentGridItem defaultColumn="span 2 / -1" mdColumn="span 4 / -1" />
 * ```
 *
 * ##### Omitting from the Grid
 *
 * Setting a column definition prop to `null` will prevent the item from
 * rendering in the grid. For example, to hide an item for narrow content
 * areas: `<ContentGridItem defaultColumn={null} wideSpan={3} />`.
 */

export const ContentGridItem = _ref => {
  let {
    className,
    children,
    ...props
  } = _ref;
  const gridContext = useContentGrid();
  const gridColumnCount = gridContext.columnCount;
  const {
    range
  } = gridContext;
  const columnDefinitions = parseRangeProps(props); // The column definition to be applied within the active range. If there is no
  // active range, then this is outside of a content area; fall back to default range.

  const activeRangeColumnDefinition = columnDefinitions.get(range !== null && range !== void 0 ? range : defaultContentAreaRange);
  const activeRangeColumnCount = activeRangeColumnDefinition && gridColumnCount ? columnsSpanned(activeRangeColumnDefinition, gridColumnCount) : null;
  const newGridContext = { ...gridContext,
    ...(gridColumnCount ? {
      // Update columnCount for children based on the number of columns
      // consumed by this item
      columnCount: activeRangeColumnCount
    } : {// Outside of a grid. Do not update columnCount
    })
  }; // Custom properties that define the columns spanned and grid-column for each range

  const styleProperties = gridColumnCount ? Array.from(columnDefinitions.entries()).reduce((propertiesAccumulator, _ref2) => {
    let [range, columnDefinition] = _ref2;
    const rangeColumnCount = columnsSpanned(columnDefinition, gridColumnCount);
    return { ...propertiesAccumulator,
      [`--${range}-column-count`]: rangeColumnCount,
      [`--${range}-grid-column`]: columnDefinition.gridColumn
    };
  }, {}) : {};
  const {
    defaultSpan,
    defaultColumn,
    xxxsColumn,
    xxxsSpan,
    xxsColumn,
    xxsSpan,
    xsColumn,
    xsSpan,
    smColumn,
    smSpan,
    wideColumn,
    wideSpan,
    mdColumn,
    mdSpan,
    lgColumn,
    lgSpan,
    xlColumn,
    xlSpan,
    xxlColumn,
    xxlSpan,
    ...divProps
  } = props;
  return /*#__PURE__*/React.createElement(React.Fragment, null, activeRangeColumnDefinition && !activeRangeColumnDefinition.isOmitted && /*#__PURE__*/React.createElement("div", Object.assign({
    className: classNames(styles.item, className),
    style: styleProperties
  }, divProps), /*#__PURE__*/React.createElement(ContentGridContext.Provider, {
    value: newGridContext
  }, children)));
};
//# sourceMappingURL=content-grid-item.react-component.js.map